import { FC, ReactElement, useMemo } from "react";
import {
  Card,
  CircularProgress,
  Stack,
  Box,
  IconButton,
  Button,
} from "@mui/material";

import { MultiStackBarChart, VendorsTable, InfoTooltip, PlaceholderCard } from "@/components";
import { FaArrowDown, FaArrowUp } from "react-icons/fa6";
import { columns } from "@/columns/COMMON/tariff-codes";
import { TARIFF_CODES_TOTAL_FIELDS, tariff_code_placeholder } from "@/constants";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";
import { spacing } from "@/utils/responsive";
import { handleCSVDownload } from "@/utils";

type ITariffCard = {
  title?: string;
  isLoading: boolean;
} & (
  | {
      // Table mode for analytics page
      data: ITariff[];
      mode?: "table";
      totalPage?: never;
      currentPage?: never;
      onPageChange?: never;
      colors?: never;
      getSeries?: never;
      getCategories?: never;
    }
  | {
      // Chart mode with pagination for potential page
      data: ITariff[];
      mode: "chart";
      totalPage: number;
      currentPage: number;
      onPageChange: (pageNo: number) => void;
      colors?: string[];
      getSeries?: never;
      getCategories?: never;
    }
  | {
      // Chart mode with custom data for potential page
      data?: never;
      mode: "chart";
      totalPage: number;
      currentPage: number;
      onPageChange: (pageNo: number) => void;
      colors?: string[];
      getSeries: () => { name: string; data: number[] }[];
      getCategories: () => (number | string)[];
    }
);

const TariffCard: FC<ITariffCard> = (props): ReactElement => {
  const {
    title = "Tariff Codes",
    isLoading,
    mode = "table",
  } = props;
  
  const { user } = useAppSelector((state) => state.auth);
  const { selectedClient } = useAppSelector((state) => state.client);
  
  const canSelectClients = useMemo(() => {
    const allowedRoles = [roles.SPOWNER, roles.SPPARTNER] as string[];
    return allowedRoles.includes(user?.userRole ?? "");
  }, [user]);

  
  const { tariffValue, tariffDuty, percentOfTotal, effectiveDutyRate, enrichedData } = useMemo(() => {
    if (mode !== "table" || !props.data) {
      return { tariffValue: 0, tariffDuty: 0, percentOfTotal: 100, effectiveDutyRate: 0, enrichedData: [] };
    }
    
    const totalValue = props.data.reduce((sum, item) => sum + (item.tariffValue || 0), 0);
    const totalDuty = props.data.reduce((sum, item) => sum + (item.tariffDuty || 0), 0);
    const effectiveDutyRate = totalValue > 0 ? (totalDuty / totalValue) * 100 : 0;
    
    const enriched = props.data.map(item => ({
      ...item,
      percentOfTotal: totalValue > 0 ? ((item.tariffValue || 0) / totalValue) * 100 : 0
    }));
    
    return {
      tariffValue: totalValue,
      tariffDuty: totalDuty,
      percentOfTotal: 100,
      effectiveDutyRate,
      enrichedData: enriched
    };
  }, [mode, props.data]);

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        maxHeight: { xs: 360, md: 400 },
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" p={{ xs: 1.5, md: 2 }}>
        <InfoTooltip title={title} text="Top HTS codes by import value" />
        {mode === "table" && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              if (!props.data) return;
              const headers = [
                "HTS Code",
                "Goods Value",
                "Customs Duty",
                "Effective Duty Rate (%)",
              ];
              const csvData = props.data.map((r) => [
                r.htsNumber,
                r.tariffValue ?? 0,
                r.tariffDuty ?? 0,
                (r.tariffValue ?? 0) > 0 ? (((r.tariffDuty ?? 0) / (r.tariffValue ?? 1)) * 100).toFixed(2) : "0.00",
              ]);
              handleCSVDownload(headers, csvData, "tariff_codes.csv");
            }}
          >
            Download CSV
          </Button>
        )}
      </Stack>
      {mode === "chart" ? (
        // Chart mode for potential page
        <Box p={{ xs: 1.5, md: 2 }}>
          {props.totalPage && props.currentPage && props.onPageChange && (
            <Stack direction="row" justifyContent="flex-end" gap={1} mb={spacing.md}>
              <IconButton
                disabled={isLoading || props.currentPage === 1}
                onClick={() => props.onPageChange(props.currentPage - 1)}
                size="small"
              >
                <FaArrowUp size="16" />
              </IconButton>
              <IconButton
                disabled={isLoading || props.currentPage === props.totalPage}
                onClick={() => props.onPageChange(props.currentPage + 1)}
                size="small"
              >
                <FaArrowDown size="16" />
              </IconButton>
            </Stack>
          )}
          {isLoading ? (
            <Stack minHeight={{ xs: 200, md: 250 }} justifyContent="center" alignItems="center">
              <CircularProgress />
            </Stack>
          ) : (
            <MultiStackBarChart
              data={
                props.getSeries
                  ? props.getSeries()
                  : props.data
                  ? [
                      {
                        name: "Tariff Duty",
                        data: props.data
                          .map(({ tariffDuty }) => tariffDuty)
                          .filter((value) => value !== null && value !== undefined),
                      },
                      {
                        name: "Tariff Value",
                        data: props.data
                          .map(({ tariffValue }) => tariffValue)
                          .filter((value) => value !== null && value !== undefined),
                      },
                    ]
                  : []
              }
              categories={
                props.getCategories
                  ? props.getCategories() as any
                  : props.data
                  ? props.data.map(({ htsNumber }) => htsNumber).filter((value) => value)
                  : []
              }
              colors={props.colors}
            />
          )}
        </Box>
      ) : canSelectClients && !selectedClient ? (
        <PlaceholderCard
          description="Visualize your top HTS codes by import value and duty impact, helping identify which classifications are driving your customs costs and trade flows."
          imgUrl={tariff_code_placeholder}
          centerContent
          isBordered
          cardStyles={{
            m: { xs: 2, md: 2.5 },
            mt: 0,
          }}
        />
      ) : (
        <VendorsTable
          columns={columns()}
          contentIdKey="htsNumber"
          data={enrichedData}
          footerColumns={TARIFF_CODES_TOTAL_FIELDS}
          footerData={{
            tariffValue,
            tariffDuty,
            effectiveDutyRate,
            percentOfTotal,
          }}
          isLoading={isLoading}
          limit={1000}
          page={1}
          total={enrichedData.length}
          fillerRowCount={0}
        />
      )}
    </Card>
  );
};

export default TariffCard;